import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { captureApiError } from '../errors/errorTracking';
import { msalInstance } from '../auth/config';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_PUBLIC_FUNCTION_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for centralized error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Capture API error to PostHog
    captureApiError(error, {
      component: 'AxiosInterceptor',
      action: 'apiRequest',
      severity: error.response?.status >= 500 ? 'error' : 'warning',
    });

    // Re-throw the error so it can be handled by the calling code
    return Promise.reject(error);
  }
);

// Request interceptor: attach MSAL Bearer token to every outgoing request.
// acquireTokenSilent uses the cached token and only hits the network when expired.
// NOTE: React hooks cannot be used here — interceptors run outside the component tree.
axiosInstance.interceptors.request.use(async (config) => {
  // getActiveAccount() is null unless setActiveAccount() was called explicitly.
  // Fall back to the first cached account — works for single-user sign-in flows.
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

  if (account) {
    try {
      const { accessToken } = await msalInstance.acquireTokenSilent({
        scopes: [`api://${import.meta.env.VITE_PUBLIC_MSAL_CLIENT_ID}/access_as_user`],
        account,
      });
      config.headers.Authorization = `Bearer ${accessToken}`;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        // consent_required / login_required — trigger the Azure AD consent dialog.
        // This redirects the page; the request won't complete until the user is back.
        await msalInstance.acquireTokenRedirect({
          scopes: [`api://${import.meta.env.VITE_PUBLIC_MSAL_CLIENT_ID}/access_as_user`],
          account,
        });
      } else {
        console.warn('acquireTokenSilent failed, sending request without token', e);
      }
    }
  }

  return config;
});

export default axiosInstance;

